import * as requests from "src/utils/request";
import * as auth from "src/utils/auth";
import getConfig from "next/config";

const { publicRuntimeConfig } = getConfig();

import type {
  Notification,
  NotificationSearchResponse,
  NotificationDetail,
  NotificationSearchParams,
} from "../types/notification";

// Define types for SSE callbacks
type SSEMessageHandler = (data: Notification) => void;
type SSEErrorHandler = (error: Error) => void;

// Global flag to prevent reconnection after 401 (module-level)
let globalShouldReconnect = true;

export const notificationApi = {
  // Connect to SSE with authentication using native fetch
  connectSSE: async (onMessage: SSEMessageHandler, onError?: SSEErrorHandler) => {
    const sseUrl = `${publicRuntimeConfig.AIR_LCL_COMMON_BFF_URL}/invoicing/api/internal/sse/connect`;

    // Check global reconnection flag before attempting connection
    if (!globalShouldReconnect) {
      console.warn("SSE reconnection is disabled due to previous auth error");
      throw new Error("Reconnection disabled: Previous authentication failure");
    }

    // Get auth token from either source
    const token = auth.getToken();

    console.log("Connecting to SSE with native fetch to:", sseUrl);
    console.log("Using token:", token ? "Yes" : "No");

    const abortController = new AbortController();
    let keepaliveTimer: NodeJS.Timeout | null = null;
    let fetchTimeout: NodeJS.Timeout | null = null;
    let shouldReconnect = true; // Local flag to control reconnection behavior

    try {
      // Add a timeout to abort fetch if it hangs (e.g., 30 seconds)
      fetchTimeout = setTimeout(() => {
        console.warn("Fetch timeout, aborting SSE connection");
        abortController.abort();
      }, 60000); // Adjust timeout as needed

      const response: Response = await fetch(sseUrl, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${token}`,
          "Cache-Control": "no-cache",
        },
        signal: abortController.signal,
      });

      // Clear the timeout since fetch succeeded
      if (fetchTimeout) clearTimeout(fetchTimeout);

      // Handle 401 - stop all reconnection attempts
      if (response.status === 401) {
        console.warn("Received 401 Unauthorized, disabling ALL future reconnections");
        globalShouldReconnect = false; // Disable ALL future reconnections (module-level)
        shouldReconnect = false; // Disable local reconnection
        abortController.abort();

        // Clear window reconnect function to prevent any reconnection attempts
        if (window.reconnectNotificationSSE) {
          window.reconnectNotificationSSE = undefined;
        }

        if (location.pathname !== "/login") {
          auth.clearToken();
          // window.location.href = "/login";
        }

        throw new Error("Unauthorized: 401");
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      console.log("Response received, setting up stream processing");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let eventName = "";

      const connection = {
        close: () => {
          console.log("Closing SSE connection");
          shouldReconnect = false; // Disable reconnection when manually closing
          if (keepaliveTimer) clearTimeout(keepaliveTimer); // Clear keepalive timer
          abortController.abort();
          reader.cancel().catch(() => {
            // Ignore errors when canceling
          });
        },
      };

      const resetKeepaliveTimer = (reconnect: () => void) => {
        if (keepaliveTimer) clearTimeout(keepaliveTimer);
        keepaliveTimer = setTimeout(() => {
          // Only reconnect if we should (not after 401)
          if (!shouldReconnect) {
            console.log(
              "Reconnection disabled (likely due to auth error), skipping keepalive reconnect"
            );
            return;
          }
          console.warn("Keepalive over 60s, reconnect SSE");
          connection.close();
          // If there is an external connectionRef.current, clear it to allow reconnection
          if (window.connectionRef && window.connectionRef.current !== undefined) {
            window.connectionRef.current = null;
          }
          reconnect();
        }, 60000);
      };
      const reconnect = () => {
        // Only reconnect if we should (not after 401)
        if (!shouldReconnect) {
          console.log("Reconnection disabled (likely due to auth error), skipping reconnect");
          return;
        }
        // notificationApi.connectSSE(onMessage, onError)
        if (window.reconnectNotificationSSE) {
          window.reconnectNotificationSSE();
        }
      };
      // Process stream in background
      const processStream = async () => {
        try {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              console.log("Stream ended");
              break;
            }

            if (!value) {
              console.log("No value in chunk, continuing...");
              continue;
            }

            const chunk = decoder.decode(value, { stream: true });
            console.log("Received chunk:", JSON.stringify(chunk));

            if (chunk.length === 0) {
              console.log("Empty chunk received, continuing...");
              continue;
            }

            buffer += chunk;
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              const normalizedLine = line.replace(/\r$/, "");
              if (normalizedLine.trim() === "") {
                eventName = "";
                continue;
              }
              console.log("Processing line:", JSON.stringify(normalizedLine));

              if (normalizedLine.startsWith("event:")) {
                eventName = normalizedLine.slice(6).trim().toLowerCase();
                continue;
              }

              if (normalizedLine.startsWith("data:")) {
                const rawData = normalizedLine.slice(5);
                const data = rawData.startsWith(" ") ? rawData.slice(1) : rawData;
                const normalizedData = data.trim().toLowerCase();
                if (
                  eventName === "heartbeat" ||
                  normalizedData === "keepalive" ||
                  normalizedData === "heartbeat"
                ) {
                  resetKeepaliveTimer(reconnect);
                  eventName = "";
                  continue;
                }
                console.log("SSE data:", JSON.stringify(data));

                if (data === "[DONE]") {
                  console.log("Received [DONE], closing connection");
                  connection.close();
                  return;
                }

                try {
                  const parsed = JSON.parse(data);
                  const parsedRecord =
                    typeof parsed === "object" && parsed !== null
                      ? (parsed as Record<string, unknown>)
                      : null;
                  const parsedEvent = String(
                    parsedRecord?.type || parsedRecord?.event || parsedRecord?.event_type || ""
                  )
                    .trim()
                    .toLowerCase();
                  if (parsedEvent === "heartbeat" || parsedEvent === "keepalive") {
                    resetKeepaliveTimer(reconnect);
                    eventName = "";
                    continue;
                  }
                  console.log("Parsed notification:", parsed);
                  onMessage(parsed);
                } catch (error) {
                  console.log("Could not parse as JSON, treating as raw data:", data);
                  onMessage({
                    id: Date.now(),
                    title: "Raw SSE Data",
                    message: data,
                    type: "NOTIFICATION",
                    isRead: false,
                    createdAt: new Date().toISOString(),
                  } as Notification);
                }
                eventName = "";
              }
            }
          }
        } catch (streamError) {
          // Ignore AbortError when connection is intentionally closed
          if (streamError instanceof Error && streamError.name === "AbortError") {
            console.log("Stream aborted (connection closed)");
            return; // Don't call onError for intentional aborts
          }
          console.error("Stream processing error:", streamError);
          if (onError) onError(streamError as Error);
        }
      };

      // Start processing
      processStream();

      console.log("SSE connection established with native fetch");
      return connection;
    } catch (error) {
      // Clear all timers in case of error
      if (fetchTimeout) clearTimeout(fetchTimeout);
      if (keepaliveTimer) clearTimeout(keepaliveTimer);

      console.error("Native fetch SSE failed:", error);

      // If it's a 401 error, make sure reconnection stays disabled
      if (error instanceof Error && error.message.includes("401")) {
        globalShouldReconnect = false; // Disable at module level
        shouldReconnect = false;
        console.log("401 error caught, reconnection permanently disabled at global level");
      }

      if (onError) onError(error as Error);
      throw error;
    }
  },

  // Reset reconnection flag (useful after re-login)
  resetReconnectionFlag: () => {
    console.log("Resetting SSE reconnection flag");
    globalShouldReconnect = true;
  },

  // Mark notifications as read
  markAsRead: (ids: number[]) => {
    return requests.post("/invoicing/api/internal/sse/mark-read", { ids });
  },

  // Search notifications
  search: (params: NotificationSearchParams): Promise<NotificationSearchResponse> => {
    const searchParams = new URLSearchParams();

    if (params.isRead !== undefined) {
      searchParams.append("isRead", params.isRead.toString());
    }
    searchParams.append("page", params.page.toString());
    searchParams.append("size", params.size.toString());

    let queryString = searchParams.toString();

    if (params.sort) {
      // Convert "id desc" to "id,desc" format for sort parameter
      const sortValue = params.sort.replace(" ", ",");
      queryString += `${queryString ? "&" : ""}sort=${sortValue}`;
    }

    return requests.get(`/invoicing/api/internal/sse/search?${queryString}`);
  },

  // Get notification detail
  getDetail: (id: number): Promise<NotificationDetail> => {
    return requests.get(`/invoicing/api/internal/sse/detail/${id}`);
  },

  getFileUrl(id: string) {
    return requests.get(`/invoicing/api/internal/cloudAttachments/${id}`);
  },
};
